import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Button, Chip, 
  List, ListItem, ListItemText, ListItemIcon, ListItemSecondaryAction,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem, Switch,
  Tooltip, Alert, CircularProgress, Divider, Badge
} from '@mui/material';
import {
  DoorFront as DoorIcon,
  Lock as LockIcon,
  LockOpen as UnlockIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  People as PeopleIcon,
  CheckCircle as OnlineIcon,
  Error as OfflineIcon,
  Warning as WarningIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import axiosInstance from '../axiosConfig';
import moment from 'moment';

const AccessControl = () => {
  const [doors, setDoors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDoor, setSelectedDoor] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState(''); // 'unlock', 'schedule', 'permissions'
  const [unlockDuration, setUnlockDuration] = useState(30);
  const [scheduleData, setScheduleData] = useState({
    startTime: '08:00',
    endTime: '18:00',
    days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
  });

  useEffect(() => {
    fetchDoors();
  }, []);

  const fetchDoors = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/unifi/access/doors');
      setDoors(response.data);
    } catch (error) {
      console.error('Error fetching doors:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUnlockDoor = async (doorId, duration = 30) => {
    try {
      await axiosInstance.post('/api/unifi/access/unlock', {
        doorId,
        duration
      });
      showSnackbar('Puerta desbloqueada exitosamente', 'success');
      fetchDoors(); // Refresh status
    } catch (error) {
      console.error('Error unlocking door:', error);
      showSnackbar('Error al desbloquear puerta', 'error');
    }
  };

  const handleLockDoor = async (doorId) => {
    try {
      await axiosInstance.post('/api/unifi/access/lock', {
        doorId
      });
      showSnackbar('Puerta bloqueada exitosamente', 'success');
      fetchDoors(); // Refresh status
    } catch (error) {
      console.error('Error locking door:', error);
      showSnackbar('Error al bloquear puerta', 'error');
    }
  };

  const handleScheduleAccess = async (doorId, schedule) => {
    try {
      await axiosInstance.post('/api/unifi/access/schedule', {
        doorId,
        schedule
      });
      showSnackbar('Horario configurado exitosamente', 'success');
      setOpenDialog(false);
    } catch (error) {
      console.error('Error setting schedule:', error);
      showSnackbar('Error al configurar horario', 'error');
    }
  };

  const handleOpenDialog = (type, door = null) => {
    setDialogType(type);
    setSelectedDoor(door);
    setOpenDialog(true);
  };

  const closeDialog = () => {
    setOpenDialog(false);
    setSelectedDoor(null);
    setDialogType('');
  };

  const showSnackbar = (message, severity = 'success') => {
    // You can implement a snackbar system here
    console.log(`${severity}: ${message}`);
  };

  const getDoorStatusColor = (status) => {
    switch (status) {
      case 'unlocked': return 'success';
      case 'locked': return 'error';
      case 'scheduled': return 'info';
      case 'offline': return 'warning';
      default: return 'default';
    }
  };

  const getDoorStatusText = (status) => {
    switch (status) {
      case 'unlocked': return 'Desbloqueada';
      case 'locked': return 'Bloqueada';
      case 'scheduled': return 'Programada';
      case 'offline': return 'Offline';
      default: return 'Desconocido';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          Control de Acceso
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Actualizar">
            <IconButton onClick={fetchDoors}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('add')}
          >
            Agregar Puerta
          </Button>
        </Box>
      </Box>

      {/* Doors Grid */}
      <Grid container spacing={3}>
        {doors.map((door) => (
          <Grid item xs={12} md={6} lg={4} key={door.id}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DoorIcon color="primary" />
                    <Typography variant="h6" component="h3">
                      {door.name}
                    </Typography>
                  </Box>
                  <Chip
                    label={getDoorStatusText(door.status)}
                    color={getDoorStatusColor(door.status)}
                    size="small"
                  />
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {door.location}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<UnlockIcon />}
                    onClick={() => handleOpenDialog('unlock', door)}
                    disabled={door.status === 'offline'}
                  >
                    Desbloquear
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<LockIcon />}
                    onClick={() => handleLockDoor(door.id)}
                    disabled={door.status === 'offline'}
                  >
                    Bloquear
                  </Button>
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="text"
                    size="small"
                    startIcon={<ScheduleIcon />}
                    onClick={() => handleOpenDialog('schedule', door)}
                  >
                    Horario
                  </Button>
                  <Button
                    variant="text"
                    size="small"
                    startIcon={<PeopleIcon />}
                    onClick={() => handleOpenDialog('permissions', door)}
                  >
                    Permisos
                  </Button>
                  <IconButton size="small" onClick={() => handleOpenDialog('edit', door)}>
                    <EditIcon />
                  </IconButton>
                </Box>

                {door.lastAccess && (
                  <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary">
                      Último acceso: {moment(door.lastAccess).format('DD/MM/YYYY HH:mm')}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Unlock Dialog */}
      <Dialog open={openDialog && dialogType === 'unlock'} onClose={closeDialog}>
        <DialogTitle>Desbloquear Puerta</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            ¿Por cuánto tiempo desea desbloquear la puerta "{selectedDoor?.name}"?
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Duración</InputLabel>
            <Select
              value={unlockDuration}
              onChange={(e) => setUnlockDuration(e.target.value)}
            >
              <MenuItem value={15}>15 segundos</MenuItem>
              <MenuItem value={30}>30 segundos</MenuItem>
              <MenuItem value={60}>1 minuto</MenuItem>
              <MenuItem value={300}>5 minutos</MenuItem>
              <MenuItem value={600}>10 minutos</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancelar</Button>
          <Button 
            onClick={() => {
              handleUnlockDoor(selectedDoor.id, unlockDuration);
              closeDialog();
            }}
            variant="contained"
          >
            Desbloquear
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schedule Dialog */}
      <Dialog open={openDialog && dialogType === 'schedule'} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Configurar Horario - {selectedDoor?.name}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                label="Hora de Apertura"
                type="time"
                value={scheduleData.startTime}
                onChange={(e) => setScheduleData({...scheduleData, startTime: e.target.value})}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Hora de Cierre"
                type="time"
                value={scheduleData.endTime}
                onChange={(e) => setScheduleData({...scheduleData, endTime: e.target.value})}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Días de la semana:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                  <Chip
                    key={day}
                    label={day.charAt(0).toUpperCase() + day.slice(1, 3)}
                    onClick={() => {
                      const newDays = scheduleData.days.includes(day)
                        ? scheduleData.days.filter(d => d !== day)
                        : [...scheduleData.days, day];
                      setScheduleData({...scheduleData, days: newDays});
                    }}
                    color={scheduleData.days.includes(day) ? 'primary' : 'default'}
                    variant={scheduleData.days.includes(day) ? 'filled' : 'outlined'}
                    size="small"
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancelar</Button>
          <Button 
            onClick={() => handleScheduleAccess(selectedDoor.id, scheduleData)}
            variant="contained"
          >
            Guardar Horario
          </Button>
        </DialogActions>
      </Dialog>

      {/* Permissions Dialog */}
      <Dialog open={openDialog && dialogType === 'permissions'} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>Gestionar Permisos - {selectedDoor?.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Configure qué empleados tienen acceso a esta puerta
          </Typography>
          {/* Employee permissions list would go here */}
          <Alert severity="info">
            La gestión de permisos se integrará con el sistema de empleados
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccessControl; 